import { useRef, useState, useEffect } from 'react'
import readNDJSONStream from 'ndjson-readablestream'
import styles from './Ask.module.css'

import {
    chatApi,
    ChatAppResponse,
    ChatAppRequest,
    ResponseMessage,
} from '../../api'
import { Answer, AnswerError, AnswerLoading } from '../../components/Answer'
import { QuestionInput } from '../../components/QuestionInput'
import { ExampleList } from '../../components/Example'
import { UserChatMessage } from '../../components/UserChatMessage'

import {
    AnalysisPanel,
    AnalysisPanelTabs,
} from '../../components/AnalysisPanel'
import { PermissionsPopup } from '../../components/MicPermissionsDialog/micpermissionsdialog'
import { ClearChatButton } from '../../components/ClearChatButton'
import { useLayoutContext } from '../../contexts/LayoutContext'
import { InfoLabel } from '../../components/InfoLabel/InfoLabel'
import { useConfig } from '../../contexts/ConfigContext'

/** Turn a failed chat-stream response into something worth showing a user. */
const describeChatFailure = async (response: Response): Promise<string> => {
    try {
        const body = await response.json()
        if (typeof body?.detail === 'string' && body.detail.trim()) {
            return body.detail
        }
        if (typeof body?.error === 'string' && body.error.trim()) {
            return body.error
        }
    } catch {
        // Not JSON — fall through to the status-based message.
    }
    return `The server couldn't answer that (error ${response.status}).`
}

const Chat = () => {
    const lastQuestionRef = useRef<string>('')
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isStreaming, setIsStreaming] = useState<boolean>(false)
    const [error, setError] = useState<unknown>()
    const [activeCitation, setActiveCitation] = useState<string>()
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<
        AnalysisPanelTabs | undefined
    >(undefined)
    const [answers, setAnswers] = useState<
        [user: string, response: ChatAppResponse][]
    >([])
    const [streamedAnswers, setStreamedAnswers] = useState<
        [user: string, response: ChatAppResponse][]
    >([])
    const [micPermissionsDenied, setMicPermissionsDenied] =
        useState<boolean>(false)
    const [clearChatDisabled, setClearChatDisabled] = useState(false)
    const [showClearChat, setShowClearChat] = useState(false)

    const { asksubtitle, asktextfieldplaceholder } = useConfig()

    const subTitleLoading = !asksubtitle

    const {
        isMobile,
        selectedAnswer,
        setSelectedAnswer,
        logoURL,
        loading: logoLoading,
        communityhubname,
        isLoadingName,
    } = useLayoutContext()

    const handleAsyncRequest = async (
        question: string,
        answers: [string, ChatAppResponse][],
        responseBody: ReadableStream<any>
    ) => {
        let answer: string = ''
        let askResponse: ChatAppResponse = {} as ChatAppResponse

        const updateState = (newContent: string) => {
            return new Promise((resolve) => {
                setTimeout(() => {
                    answer += newContent
                    const latestResponse: ChatAppResponse = {
                        // `askResponse` is only populated by the `citations`
                        // event. A stream that goes straight to deltas (or
                        // fails early) leaves it empty, so the role has to fall
                        // back rather than be read off `message` blindly.
                        ...askResponse,
                        message: {
                            content: answer,
                            role: askResponse.message?.role ?? 'assistant',
                        },
                    }
                    setStreamedAnswers([...answers, [question, latestResponse]])
                    resolve(null)
                }, 33)
            })
        }
        try {
            setIsStreaming(true)
            for await (const event of readNDJSONStream(responseBody)) {
                if (event['type'] === 'status') {
                    continue
                } else if (event['type'] === 'done') {
                    askResponse.session_state =
                        event['conversation_id'] ?? askResponse.session_state
                    askResponse.assistant_message_id =
                        event['assistant_message_id'] ??
                        event['message_id'] ??
                        askResponse.assistant_message_id
                    if (event['context']?.followup_questions) {
                        askResponse.context = {
                            ...askResponse.context,
                            followup_questions:
                                event['context']['followup_questions'],
                        }
                    }
                } else if (
                    event['context'] &&
                    event['context']['data_points']
                ) {
                    event['message'] = event['delta']
                    askResponse = event as ChatAppResponse
                } else if (event['delta']?.['content']) {
                    setIsLoading(false)
                    await updateState(event['delta']['content'])
                } else if (event['context']) {
                    // Update context with new keys from latest event
                    askResponse.context = {
                        ...askResponse.context,
                        ...event['context'],
                    }
                } else if (event['error']) {
                    throw Error(event['error'])
                }
            }
        } finally {
            setIsStreaming(false)
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            message: {
                content: answer,
                role: askResponse.message?.role ?? 'assistant',
            },
        }
        return fullResponse
    }

    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question
        error && setError(undefined)
        setIsLoading(true)
        setActiveCitation(undefined)
        setActiveAnalysisPanelTab(undefined)

        const token = undefined

        try {
            const messages: ResponseMessage[] = answers.flatMap((a) => [
                { content: a[0], role: 'user' },
                { content: a[1].message.content, role: 'assistant' },
            ])

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: 'user' }],
                context: {},
                // AI Chat Protocol: Client must pass on any session state received from the server
                session_state: answers.length
                    ? answers[answers.length - 1][1].session_state
                    : null,
            }

            const response = await chatApi(request, true, token)
            // The stream endpoint replies with a plain JSON body on failure
            // (401, 429, 5xx), not NDJSON. Without this check that body was
            // handed to the NDJSON reader, matched none of the event branches
            // below, and the first read of `askResponse.message` threw
            // "Cannot read properties of undefined (reading 'role')" — so the
            // user saw a TypeError instead of what the server actually said.
            if (!response.ok) {
                throw Error(await describeChatFailure(response))
            }
            if (!response.body) {
                throw Error('No response body')
            }
            const parsedResponse: ChatAppResponse = await handleAsyncRequest(
                question,
                answers,
                response.body
            )
            setAnswers([...answers, [question, parsedResponse]])
        } catch (e) {
            setError(e)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        setClearChatDisabled(isStreaming || isLoading)
    }, [isStreaming, isLoading])

    useEffect(() => {
        setShowClearChat(lastQuestionRef.current == '')
    }, [lastQuestionRef.current])

    const clearChat = () => {
        lastQuestionRef.current = ''
        error && setError(undefined)
        setActiveCitation(undefined)
        setActiveAnalysisPanelTab(undefined)
        setAnswers([])
        setStreamedAnswers([])
        setIsLoading(false)
        setIsStreaming(false)
    }

    // Block bodies, not concise arrows: a concise arrow returns whatever the
    // call evaluates to, and React treats any non-undefined return as the
    // effect's cleanup — then calls it on unmount. `scrollIntoView` happens to
    // return undefined, so this was benign, but it is one refactor away from a
    // "destroy is not a function" crash during the commit phase.
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
    }, [isLoading])
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'auto' })
    }, [streamedAnswers])

    const onExampleClicked = (example: string) => {
        makeApiRequest(example)
    }

    const onShowCitation = (citation: string, index: number) => {
        if (
            activeCitation === citation &&
            activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab &&
            selectedAnswer === index
        ) {
            setActiveAnalysisPanelTab(undefined)
        } else {
            setActiveCitation(citation)
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab)
        }

        setSelectedAnswer(index)
    }

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined)
        } else {
            setActiveAnalysisPanelTab(tab)
        }

        setSelectedAnswer(index)
    }

    return (
        <div className={styles.container}>
            <PermissionsPopup
                setMicPermissionsDenied={setMicPermissionsDenied}
            />
            <div className={styles.commandsContainer}></div>
            <div className={styles.chatRoot}>
                <div
                    className={`${styles.chatAndPanelContainer} ${activeAnalysisPanelTab ? styles.panelOpen : ''}`}
                >
                    <div className={styles.chatContainer}>
                        {!lastQuestionRef.current ? (
                            <div className={styles.chatEmptyState}>
                                <div className={styles.chatEmptyStateLogo}>
                                    {logoLoading || !logoURL ? (
                                        <div
                                            className="skeleton"
                                            style={{
                                                width: isMobile ? 120 : 150,
                                                height: isMobile ? 120 : 150,
                                                borderRadius: '12px',
                                            }}
                                        />
                                    ) : (
                                        <img
                                            src={logoURL}
                                            width={isMobile ? 120 : 150}
                                            height="auto"
                                            alt="Logo"
                                        />
                                    )}
                                    <h1
                                        className={styles.chatEmptyStateTitle}
                                        style={
                                            isMobile
                                                ? { display: 'none' }
                                                : { display: 'block' }
                                        }
                                    >
                                        {isLoadingName ? (
                                            <div
                                                className="skeleton"
                                                style={{
                                                    width: '150px',
                                                    height: '30px',
                                                }}
                                            ></div>
                                        ) : (
                                            communityhubname
                                        )}
                                    </h1>
                                </div>

                                <div className={styles.chatEmptyStateQuestions}>
                                    <div
                                        className={
                                            styles.chatEmptyStateSubtitle
                                        }
                                    >
                                        {subTitleLoading ? (
                                            <div
                                                className="skeleton"
                                                style={{
                                                    width: '150px',
                                                    height: '20px',
                                                    justifySelf: 'center',
                                                }}
                                            ></div>
                                        ) : (
                                            <InfoLabel
                                                info="This is a specialist version of Lorefully, trained using specific content only. By design, it's been limited to this knowledge in order to give you the most reliable and accurate answers possible. It can't talk to you about lighthouses, lobsters or linguine."
                                                label={asksubtitle}
                                                className={styles.infoLabel}
                                                iconSize={16}
                                            />
                                        )}
                                    </div>
                                    <ExampleList
                                        onExampleClicked={onExampleClicked}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className={styles.chatMessageStream}>
                                <div
                                    className={styles.headerContainer}
                                >
                                    <div
                                        style={
                                            showClearChat
                                                ? { display: 'none' }
                                                : { display: 'block' }
                                        }
                                        className={styles.clearChatMobile}
                                    >
                                        <div
                                            className={
                                                styles.clearChatMobileContainer
                                            }
                                        >
                                            <ClearChatButton
                                                onClick={clearChat}
                                                disabled={clearChatDisabled}
                                            />
                                            <h3
                                                className={
                                                    styles.clearChatMobileText
                                                }
                                            >
                                                Clear Chat
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                                {isStreaming &&
                                    streamedAnswers.map(
                                        (streamedAnswer, index) => (
                                            <div
                                                key={index}
                                                className={
                                                    styles.streamContainer
                                                }
                                            >
                                                <UserChatMessage
                                                    message={streamedAnswer[0]}
                                                />
                                                <div
                                                    className={
                                                        styles.chatMessageGpt
                                                    }
                                                >
                                                    <Answer
                                                        isStreaming={true}
                                                        key={index}
                                                        answer={
                                                            streamedAnswer[1]
                                                        }
                                                        isSelected={false}
                                                        onCitationClicked={(
                                                            c
                                                        ) =>
                                                            onShowCitation(
                                                                c,
                                                                index
                                                            )
                                                        }
                                                        onFeedbackClicked={() =>
                                                            onToggleTab(
                                                                AnalysisPanelTabs.FeedbackTab,
                                                                index
                                                            )
                                                        }
                                                        onFollowupQuestionClicked={(
                                                            q
                                                        ) => makeApiRequest(q)}
                                                        showFollowupQuestions={
                                                            answers.length -
                                                                1 ===
                                                            index
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )
                                    )}
                                {!isStreaming &&
                                    answers.map((answer, index) => (
                                        <div key={index}>
                                            <UserChatMessage
                                                message={answer[0]}
                                            />
                                            <div
                                                className={
                                                    styles.chatMessageGpt
                                                }
                                            >
                                                <Answer
                                                    isStreaming={false}
                                                    key={index}
                                                    answer={answer[1]}
                                                    isSelected={
                                                        selectedAnswer ===
                                                            index &&
                                                        activeAnalysisPanelTab !==
                                                            undefined
                                                    }
                                                    onCitationClicked={(c) =>
                                                        onShowCitation(c, index)
                                                    }
                                                    onFeedbackClicked={() =>
                                                        onToggleTab(
                                                            AnalysisPanelTabs.FeedbackTab,
                                                            index
                                                        )
                                                    }
                                                    onFollowupQuestionClicked={(
                                                        q
                                                    ) => makeApiRequest(q)}
                                                    showFollowupQuestions={
                                                        answers.length - 1 ===
                                                        index
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                {isLoading && (
                                    <>
                                        <UserChatMessage
                                            message={lastQuestionRef.current}
                                        />
                                        <div
                                            className={
                                                styles.chatMessageGptMinWidth
                                            }
                                        >
                                            <AnswerLoading />
                                        </div>
                                    </>
                                )}
                                {error ? (
                                    <>
                                        <UserChatMessage
                                            message={lastQuestionRef.current}
                                        />
                                        <div
                                            className={
                                                styles.chatMessageGptMinWidth
                                            }
                                        >
                                            <AnswerError
                                                error={error.toString()}
                                                onRetry={() =>
                                                    makeApiRequest(
                                                        lastQuestionRef.current
                                                    )
                                                }
                                            />
                                        </div>
                                    </>
                                ) : null}
                                <div ref={chatMessageStreamEnd} />
                            </div>
                        )}
                        <div className={styles.chatInput}>
                            <QuestionInput
                                clearOnSend
                                placeholder={asktextfieldplaceholder}
                                disabled={isLoading}
                                onSend={(question) => makeApiRequest(question)}
                                micPermissionsDenied={micPermissionsDenied}
                            />
                        </div>
                    </div>

                    {answers.length > 0 && activeAnalysisPanelTab && (
                        <AnalysisPanel
                            className={styles.chatAnalysisPanel}
                            activeCitation={activeCitation}
                            onActiveTabChanged={(x) =>
                                onToggleTab(x, selectedAnswer)
                            }
                            citationHeight="810px"
                            answer={answers[selectedAnswer][1]}
                            activeTab={activeAnalysisPanelTab}
                            onToggleTab={onToggleTab}
                            selectedAnswer={selectedAnswer}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default Chat
